import React, { useState, useEffect, useMemo } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Modal from '../Popup/Modal';
import DropdownFilter from '../Search/DropdownFilter';
import CustomFilters from './CustomFilters';
import './ViewConfigModal.css';
import { useItemCategories, useAllSubcategories, useProjects, useCustomFields } from '../../hooks/useQueries';
import { useHasRole } from '../../hooks/useHasRole';
import {
  customFieldColumnKey,
  customFieldFilterOptions,
  FILTER_FIELD_ALIASES
} from '../../utils/customFieldUtils';

const normalizeVisibleColumns = (value, allColumnKeys) => {
  const fallback = allColumnKeys.length > 0 ? allColumnKeys : ['id'];
  let parsedColumns = null;

  if (Array.isArray(value)) {
    parsedColumns = value;
  }

  if (!parsedColumns && typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        parsedColumns = parsed;
      }
    } catch (error) {
      console.warn('Failed to parse visible_columns:', error);
    }
  }

  const candidate = Array.isArray(parsedColumns) ? parsedColumns : fallback;
  const deduped = [];
  const seen = new Set();

  candidate.forEach((key) => {
    if (typeof key !== 'string') return;
    if (!allColumnKeys.includes(key)) return;
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(key);
  });

  if (!seen.has('id') && allColumnKeys.includes('id')) {
    deduped.unshift('id');
    seen.add('id');
  }

  if (deduped.length === 0) {
    return fallback;
  }

  return deduped;
};

function SortableSelectedColumn({ columnKey, label, disabled = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: columnKey, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    // The whole row is the drag activator, so it can be grabbed anywhere. The grip
    // stays as an affordance only - a nested button would steal focus from the row,
    // which is what dnd-kit's keyboard sensor listens on.
    <div
      ref={setNodeRef}
      style={style}
      className={`selected-column-chip ${disabled ? 'disabled' : ''} ${isDragging ? 'dragging' : ''}`}
      aria-label={disabled ? undefined : `Reorder ${label}`}
      {...attributes}
      {...listeners}
    >
      <span className="selected-column-handle" aria-hidden="true">
        <i className="bi bi-grip-vertical"></i>
      </span>
      <span className="selected-column-label">{label}</span>
      {disabled && <span className="selected-column-lock">Required</span>}
    </div>
  );
}

export default function ViewConfigModal({ 
  show, 
  onClose, 
  onSave, 
  onDelete,
  tableName, 
  columns, 
  currentConfig, 
  availableFilters 
}) {
  const [viewName, setViewName] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [customFilters, setCustomFilters] = useState([]);
  
  // Fetch all available options for dropdown fields
  const { data: allCategories = [] } = useItemCategories();
  const { data: allSubcategories = [] } = useAllSubcategories();
  const { data: allProjects = [] } = useProjects();
  // include_options so yes/no and select custom fields can offer real dropdown values
  const { data: allCustomFields = [] } = useCustomFields(null, true);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');
  const [validationErrors, setValidationErrors] = useState({});
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [columnSearchTerm, setColumnSearchTerm] = useState('');
  const [isDefaultView, setIsDefaultView] = useState(false);
  const [isGlobalView, setIsGlobalView] = useState(false);
  const [isSelectedOrderExpanded, setIsSelectedOrderExpanded] = useState(false);
  const { hasRole: isAdmin } = useHasRole('admin');
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const allColumnKeys = useMemo(() => columns.map(col => col.key), [columns]);
  const filteredColumns = useMemo(() => {
    const normalizedSearch = columnSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return columns;
    }

    return columns.filter((column) => {
      const label = String(column?.label || '').toLowerCase();
      const key = String(column?.key || '').toLowerCase();
      return label.includes(normalizedSearch) || key.includes(normalizedSearch);
    });
  }, [columns, columnSearchTerm]);
  const columnLabelMap = useMemo(
    () => Object.fromEntries(columns.map((column) => [column.key, column.label || column.key])),
    [columns]
  );
  const selectedColumnItems = useMemo(
    () => selectedColumns.map((key) => ({ key, label: columnLabelMap[key] || key })),
    [selectedColumns, columnLabelMap]
  );

  // Initialize state from currentConfig when in edit mode, or defaults for new view
  useEffect(() => {
    if (show) {
      if (currentConfig) {
        // Edit mode - populate with existing data
        setViewName(currentConfig.view_name || '');
        setSelectedColumns(normalizeVisibleColumns(currentConfig.visible_columns, allColumnKeys));
        setFilterValues(currentConfig.filters || {});
        
        // Parse custom_filters if it's a JSON string
        let parsedCustomFilters = [];
        if (currentConfig.custom_filters) {
          if (typeof currentConfig.custom_filters === 'string') {
            try {
              parsedCustomFilters = JSON.parse(currentConfig.custom_filters);
            } catch (e) {
              console.warn('Failed to parse custom_filters:', e);
              parsedCustomFilters = [];
            }
          } else if (Array.isArray(currentConfig.custom_filters)) {
            parsedCustomFilters = currentConfig.custom_filters;
          }
        }
        setCustomFilters(parsedCustomFilters);
        
        setSortField(currentConfig.sort_config?.key || 'created_on');
        setSortDirection(currentConfig.sort_config?.direction || 'desc');
        setIsDefaultView(currentConfig.is_default === true);
        setIsGlobalView(currentConfig.is_global === true);
      } else {
        // Create mode - set sensible defaults
        setViewName('');
        // Default to a concise starter set (only those that exist for this table);
        // fall back to all columns if none of the preferred keys are present.
        const preferredDefaultKeys = ['id', 'title', 'description', 'status', 'primary_owner', 'risk_zone', 'current_stage_order'];
        const defaultSelected = preferredDefaultKeys.filter((key) => allColumnKeys.includes(key));
        setSelectedColumns(defaultSelected.length > 0 ? defaultSelected : allColumnKeys);
        setFilterValues({});
        setCustomFilters([]);
        setSortField('created_on');
        setSortDirection('desc');
        setIsDefaultView(false);
        setIsGlobalView(false);
      }
      setValidationErrors({});
      setAttemptedSave(false);
      setColumnSearchTerm('');
      setIsSelectedOrderExpanded(false);
    }
  }, [show, currentConfig, columns, allColumnKeys]);

  const validateForm = () => {
    const errors = {};
    
    if (!viewName.trim()) {
      errors.viewName = 'View name is required';
    }
    
    if (selectedColumns.length === 0) {
      errors.columns = 'At least one column must be selected';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    console.log('ViewConfigModal handleSave called');
    setAttemptedSave(true);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    const viewConfig = {
      view_name: viewName.trim(),
      table_name: tableName,
      visible_columns: selectedColumns,
      filters: filterValues,
      custom_filters: customFilters,
      sort_config: { key: sortField, direction: sortDirection },
      is_default: isDefaultView
    };

    // Only admins can share a view; never send the flag otherwise, so a non-admin
    // editing someone's global view can't accidentally unshare it.
    if (isAdmin) {
      viewConfig.is_global = isGlobalView;
    }

    // If editing, include the view id
    if (currentConfig?.id) {
      viewConfig.id = currentConfig.id;
    }

    console.log('ViewConfigModal calling onSave with:', viewConfig);
    onSave(viewConfig);
  };

  const handleColumnToggle = (columnKey) => {
    if (columnKey === 'id') return; // ID column always visible
    
    setSelectedColumns(prev => 
      prev.includes(columnKey)
        ? prev.filter(key => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleColumnDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId === 'id' || overId === 'id') {
      return;
    }

    setSelectedColumns((previous) => {
      const oldIndex = previous.indexOf(activeId);
      const newIndex = previous.indexOf(overId);
      if (oldIndex < 0 || newIndex < 0) {
        return previous;
      }
      return arrayMove(previous, oldIndex, newIndex);
    });
  };

  const handleSelectAllColumns = () => {
    if (selectedColumns.length === allColumnKeys.length) {
      // Deselect all except ID
      setSelectedColumns(['id']);
    } else {
      // Select all
      setSelectedColumns(allColumnKeys);
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilterValues(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const clearAllFilters = () => {
    setFilterValues({});
    setCustomFilters([]);
  };

  const handleCustomFiltersChange = (newCustomFilters) => {
    setCustomFilters(newCustomFilters);
  };

  // Prepare field options for CustomFilters component
  const getAvailableFieldOptions = () => {
    const options = {};

    // Add options for yes/no fields
    const yesNoFields = ['exportable'];
    yesNoFields.forEach(field => {
      options[field] = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' }
      ];
    });

    // Custom fields are keyed `custom__<name>`. yes/no and select fields get real
    // dropdown values; without this they fall back to a free-text input.
    (Array.isArray(allCustomFields) ? allCustomFields : []).forEach(field => {
      const fieldOptions = customFieldFilterOptions(field);
      if (!fieldOptions) return;

      const key = customFieldColumnKey(field);
      if (key !== 'custom__') {
        options[key] = fieldOptions;
      }
    });

    // Add status options
    options['status'] = [
      { value: 'open', label: 'Open' },
      { value: 'closed', label: 'Closed' }
    ];

    // Add approval status options  
    options['approval_status'] = [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' }
    ];

    // Add all available categories
    if (allCategories.length > 0) {
      options['category_name'] = allCategories.map(cat => ({
        value: cat.categoryname || cat.name || cat,
        label: cat.categoryname || cat.name || cat
      }));
    }

    // Add all available subcategories  
    if (allSubcategories.length > 0) {
      options['subcategory_name'] = allSubcategories.map(subcat => ({
        value: subcat.subcategoryname || subcat.name || subcat,
        label: subcat.subcategoryname || subcat.name || subcat
      }));
    }

    // Add all available projects
    if (allProjects.length > 0) {
      options['project_name'] = allProjects.map(project => ({
        value: project.name || project,
        label: project.name || project
      }));
    }

    // Add options from available filters (owners, stages, etc.)
    (availableFilters || []).forEach(filter => {
      const filterKey = getFilterKey(filter);
      if (filterKey !== 'unknown' && filter.options && filter.options.length > 0) {
        // Don't override if we already have better data from API
        if (!options[filterKey]) {
          options[filterKey] = filter.options.map(option => ({
            value: option.value || option.label || option,
            label: option.label || option.value || option
          }));
        }
      }
    });

    // Columns keyed by a sort field (Stage -> current_stage_order, Task Order ->
    // project_id) filter on the value the column displays, so they reuse that
    // field's options instead of falling back to a free-text box.
    Object.entries(FILTER_FIELD_ALIASES).forEach(([columnKey, valueKey]) => {
      if (!options[columnKey] && options[valueKey]) {
        options[columnKey] = options[valueKey];
      }
    });

    return options;
  };

  const getFilterKey = (filter) => {
    // Extract filter key from placeholder (e.g., "All Owners" -> "owner")
    const placeholder = filter.placeholder.toLowerCase();
    if (placeholder.includes('statuses') || placeholder.includes('status')) return 'status';
    if (placeholder.includes('assignees') || placeholder.includes('assignee')) return 'assigned_to';
    if (placeholder.includes('roles') || placeholder.includes('role')) return 'assigned_to_role_name';
    if (placeholder.includes('types') || placeholder.includes('type')) return 'type';
    if (placeholder.includes('owners')) return 'owner';
    if (placeholder.includes('creators')) return 'created_by';
    if (placeholder.includes('categories')) return 'category';
    // 'projects' still matched: saved views created before the Task Order rename
    if (placeholder.includes('projects') || placeholder.includes('task orders')) return 'project';
    if (placeholder.includes('stages')) return 'stage';
    return 'unknown';
  };

  const modalTitle = currentConfig ? 'Edit View' : 'Create New View';
  // A global view is read-only for non-admins. The dropdown already hides the edit
  // affordances, so this only matters if the modal is reached another way.
  const canEditCurrentView = !currentConfig || currentConfig.can_edit !== false;
  const isFormValid = viewName.trim() && selectedColumns.length > 0 && canEditCurrentView;
  const isProtectedSystemView = currentConfig && (
    currentConfig.view_name === 'My Tasks' || currentConfig.view_name === 'My Items'
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={modalTitle}
      confirmText="Save View"
      cancelText="Cancel"
      onConfirm={handleSave}
      onCancel={onClose}
      confirmDisabled={!isFormValid}
      size="modal-xl"
      content={
        <div className="view-config-modal-content">
          
          {/* Section 1: View Name Input */}
          <div className="view-config-section">
            <div className="section-header">
              <label htmlFor="view-name-input" className="view-config-section-label">
                View Name<span className="required-indicator">*</span>
              </label>
            </div>
            <input
              id="view-name-input"
              type="text"
              className={`view-name-input ${attemptedSave && validationErrors.viewName ? 'error' : ''}`}
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              placeholder="Enter a name for this view..."
            />
            {attemptedSave && validationErrors.viewName && (
              <div className="validation-error">
                <i className="bi bi-exclamation-circle"></i>
                {validationErrors.viewName}
              </div>
            )}
            <label className="default-view-checkbox">
              <input
                type="checkbox"
                checked={isDefaultView}
                onChange={(e) => setIsDefaultView(e.target.checked)}
              />
              <span>Set as my default view</span>
            </label>
            {isAdmin && (
              <label className="default-view-checkbox">
                <input
                  type="checkbox"
                  checked={isGlobalView}
                  onChange={(e) => setIsGlobalView(e.target.checked)}
                />
                <span>
                  <i className="bi bi-globe2 global-view-checkbox-icon"></i>
                  Make this a global view
                </span>
              </label>
            )}
            {isAdmin && isGlobalView && (
              <div className="global-view-hint">
                <i className="bi bi-info-circle"></i>
                <span>
                  Every user sees this view. Only admins can change or delete it, but
                  anyone can select it and make it their own default.
                </span>
              </div>
            )}
          </div>

          {/* Section 2: Column Visibility Selection */}
          <div className="view-config-section">
            <div className="section-header">
              <h3 className="view-config-section-label">Columns</h3>
              <button 
                type="button"
                className="select-all-button"
                onClick={handleSelectAllColumns}
              >
                {selectedColumns.length === allColumnKeys.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="visible-columns-search-row">
              <input
                type="text"
                className="visible-columns-search-input"
                value={columnSearchTerm}
                onChange={(e) => setColumnSearchTerm(e.target.value)}
                placeholder="Search Columns"
                aria-label="Search visible columns"
              />
            </div>
            <div className="selected-columns-ordering">
              <div className="selected-columns-header-row">
                <p className="selected-columns-title">Selected Order</p>
                <button
                  type="button"
                  className="selected-columns-toggle-btn"
                  onClick={() => setIsSelectedOrderExpanded((prev) => !prev)}
                  aria-expanded={isSelectedOrderExpanded}
                  aria-controls="selected-columns-order-list"
                >
                  <i className={`bi ${isSelectedOrderExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  {isSelectedOrderExpanded ? 'Collapse' : 'Expand to reorder'}
                </button>
              </div>

              {isSelectedOrderExpanded && (
                selectedColumnItems.length > 0 ? (
                  <div id="selected-columns-order-list">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      modifiers={[restrictToVerticalAxis]}
                      onDragEnd={handleColumnDragEnd}
                    >
                      <SortableContext
                        items={selectedColumnItems.map((item) => item.key)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="selected-columns-list">
                          {selectedColumnItems.map((item) => (
                            <SortableSelectedColumn
                              key={item.key}
                              columnKey={item.key}
                              label={item.label}
                              disabled={item.key === 'id'}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                ) : (
                  <div className="selected-columns-empty">Select at least one column.</div>
                )
              )}
            </div>
            <div className="column-checkbox-grid">
              {filteredColumns.map(column => (
                <div 
                  key={column.key} 
                  className={`column-checkbox-item ${column.key === 'id' ? 'disabled' : ''}`}
                >
                  <input
                    type="checkbox"
                    id={`column-${column.key}`}
                    checked={selectedColumns.includes(column.key)}
                    onChange={() => handleColumnToggle(column.key)}
                    disabled={column.key === 'id'}
                  />
                  <label htmlFor={`column-${column.key}`}>
                    {column.label}
                  </label>
                </div>
              ))}
            </div>
            {filteredColumns.length === 0 && (
              <div className="visible-columns-empty-search">
                No columns match "{columnSearchTerm}".
              </div>
            )}
            {attemptedSave && validationErrors.columns && (
              <div className="validation-error">
                <i className="bi bi-exclamation-circle"></i>
                {validationErrors.columns}
              </div>
            )}
            {selectedColumns.length === 0 && (
              <div className="validation-warning">
                <i className="bi bi-exclamation-triangle"></i>
                At least one column must be visible
              </div>
            )}
          </div>

          {/* Section 3: Filter Configuration */}
          <div className="view-config-section">
            <div className="section-header">
              <h3 className="view-config-section-label">Quick Filters</h3>
              <button 
                type="button"
                className="clear-filters-button"
                onClick={clearAllFilters}
              >
                Clear All Filters
              </button>
            </div>
            <p className="view-config-section-subtitle">Configure default dropdown filters for this view</p>
            <div className="filter-config-container">
              {(availableFilters || []).map((filter, index) => {
                const filterKey = getFilterKey(filter);
                const currentValue = filterValues[filterKey];
                const selectedOption = filter.isMulti === true
                  ? filter.options.filter(opt => {
                    if (Array.isArray(currentValue)) {
                      return currentValue.includes(opt.value);
                    }
                    return currentValue ? opt.value === currentValue : false;
                  })
                  : (currentValue
                    ? filter.options.find(opt => opt.value === currentValue) || null
                    : null);
                
                return (
                  <DropdownFilter
                    key={index}
                    icon={filter.icon}
                    options={filter.options}
                    value={selectedOption}
                    onChange={(selectedValue) => {
                      if (filter.isMulti === true) {
                        const values = Array.isArray(selectedValue) ? selectedValue.map(option => option.value) : [];
                        handleFilterChange(filterKey, values);
                        return;
                      }
                      handleFilterChange(filterKey, selectedValue ? selectedValue.value : '');
                    }}
                    placeholder={filter.placeholder}
                    isMulti={filter.isMulti === true}
                    selectStyles={{
                      menuPortal: (base) => ({ ...base, zIndex: 1060 }),
                      menu: (base) => ({ ...base, zIndex: 1060 })
                    }}
                  />
                );
              })}
            </div>
          </div>

          {/* Section 4: Custom Advanced Filters */}
          <div className="view-config-section">
            <CustomFilters
              columns={columns}
              filters={customFilters}
              onChange={handleCustomFiltersChange}
              availableFieldOptions={getAvailableFieldOptions()}
            />
          </div>

          {/* Section 5: Sort Configuration */}
          <div className="view-config-section">
            <h3 className="view-config-section-label">Default Sort</h3>
            <div className="sort-config-container">
              <div className="sort-field-dropdown">
                <DropdownFilter
                  icon={() => <i className="bi bi-sort-down"></i>}
                  options={columns.map(col => ({ value: col.key, label: col.label }))}
                  value={sortField ? { value: sortField, label: columns.find(col => col.key === sortField)?.label || sortField } : null}
                  onChange={(selectedOption) => setSortField(selectedOption ? selectedOption.value : '')}
                  placeholder="Select sort field..."
                  selectStyles={{
                    menuPortal: (base) => ({ ...base, zIndex: 1060 }),
                    menu: (base) => ({ ...base, zIndex: 1060 })
                  }}
                />
              </div>
              <div className="sort-direction-dropdown">
                <DropdownFilter
                  icon={() => <i className="bi bi-arrow-down-up"></i>}
                  options={[
                    { value: 'asc', label: 'Ascending' },
                    { value: 'desc', label: 'Descending' }
                  ]}
                  value={sortDirection ? { value: sortDirection, label: sortDirection === 'asc' ? 'Ascending' : 'Descending' } : null}
                  onChange={(selectedOption) => setSortDirection(selectedOption ? selectedOption.value : 'desc')}
                  placeholder="Select direction..."
                  selectStyles={{
                    menuPortal: (base) => ({ ...base, zIndex: 1060 }),
                    menu: (base) => ({ ...base, zIndex: 1060 })
                  }}
                />
              </div>
            </div>
          </div>

          {currentConfig && onDelete && !isProtectedSystemView && canEditCurrentView && (
            <div className="view-config-section" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => onDelete(currentConfig)}
                style={{ width: '100%' }}
              >
                <i className="bi bi-trash" style={{ marginRight: '0.5rem' }}></i>
                Delete View
              </button>
            </div>
          )}

        </div>
      }
    />
  );
}